import { Flex, Text } from '@chakra-ui/react';
import capitalize from 'lodash-es/capitalize';
import { useIntl } from 'react-intl';
import {
    format12Hour,
    formatStoreHours,
    getHoliday,
    isClosedDay,
} from '../../../bff-api/utils/store-utils';
import type { Store } from '../../../bff-api/stores/model';
import type { Day } from '../../../bff-api/stores/model/Day';

interface Props {
    id: string;
    store: Store;
}

/**
 * This component should be removed once the footer is migrated to the new design.
 *
 * Use app\components\store-hours\StoreHours.tsx instead.
 */
export const FooterStoreHours = ({
    id,
    store: { c_storeHoursData },
}: Props): React.ReactElement | null => {
    const intl = useIntl();

    if (!c_storeHoursData?.standardHours) {
        return null;
    }

    const { standardHours, holidayHours, closedDates } = c_storeHoursData;
    const days = Object.keys(standardHours);
    const closedLabel = intl.formatMessage({
        id: 'store.item.closed',
        defaultMessage: 'closed',
    });

    return (
        <Flex data-testid={id} direction="column" gap={1}>
            <Flex data-testid={`${id}-value`} direction="column" gap={0.5}>
                {days.map((d) => {
                    const dayLowerCase = d.toLowerCase();
                    const day = d as Day;
                    const isClosed = isClosedDay(day, closedDates);

                    let formattedHours = closedLabel;

                    let holiday;

                    if (!isClosed) {
                        holiday = getHoliday(day, holidayHours);

                        if (holiday) {
                            formattedHours = format12Hour(holiday.startTime, holiday.endTime);
                        } else {
                            formattedHours = formatStoreHours(standardHours, day);
                        }
                    }

                    const capitalizedDay = capitalize(dayLowerCase);

                    return (
                        <Flex
                            data-testid={`${id}-${dayLowerCase}`}
                            key={day}
                            justifyContent="space-between"
                        >
                            <Text
                                data-testid={`${id}-${dayLowerCase}-label`}
                                textStyle="slt-former-body"
                                color="slt-pepper.1000"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                {capitalizedDay}
                            </Text>

                            {holiday ? (
                                <Flex direction="column">
                                    <Text
                                        data-testid={`${id}-${dayLowerCase}-value`}
                                        textStyle="slt-former-body"
                                        color="slt-pepper.1000"
                                        lineHeight="20px"
                                        letterSpacing="0.5px"
                                        textAlign="end"
                                        fontWeight={700}
                                    >
                                        {formattedHours}
                                    </Text>

                                    <Text
                                        data-testid={`${id}-${dayLowerCase}-holiday`}
                                        textStyle="slt-former-body"
                                        color="slt-pepper.1000"
                                        lineHeight="20px"
                                        letterSpacing="0.5px"
                                        textAlign="end"
                                        fontWeight={700}
                                    >
                                        ({holiday.label})
                                    </Text>
                                </Flex>
                            ) : (
                                <Text
                                    data-testid={`${id}-${dayLowerCase}-value`}
                                    textStyle="slt-former-body"
                                    color="slt-pepper.1000"
                                    lineHeight="20px"
                                    letterSpacing="0.5px"
                                    textAlign="end"
                                    fontWeight={700}
                                >
                                    {formattedHours}
                                </Text>
                            )}
                        </Flex>
                    );
                })}
            </Flex>
        </Flex>
    );
};
